import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import localeText from "../../../../../locale";
import BankAccountService from "../../../../../service/BankAccountService";
import OpenIbanService from "../../../../../service/OpenIbanService";
import { useToastContext } from "../../../../../contexts/ToastContext/ToastContext";
import { useThemeContext } from "../../../../../contexts/ThemeContext/ThemeContext";
import "./BankAccounts.css";

const TYPE_OPTIONS = [
  { label: "PayPal", value: "PAYPAL" },
  { label: "Banco", value: "BANK" },
];

const BankAccountFormModal = ({ visible, onHide, editingAccount, onSuccess }) => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const bankAccountService = new BankAccountService();
  const openIbanService = new OpenIbanService();
  const { theme } = useThemeContext();
  const [type, setType] = useState("PAYPAL");
  const [paypalEmail, setPaypalEmail] = useState("");
  const [accountOwner, setAccountOwner] = useState("");
  const [bankname, setBankname] = useState("");
  const [iban, setIban] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isEdit = !!editingAccount?.id;

  useEffect(() => {
    if (editingAccount) {
      setType(editingAccount.type || "PAYPAL");
      setPaypalEmail(editingAccount.paypalEmail || "");
      setAccountOwner(editingAccount.accountOwner || "");
      setBankname(editingAccount.bankname || "");
      setIban(editingAccount.iban || "");
      setIsDefault(!!editingAccount.isDefault);
    } else {
      setType("PAYPAL");
      setPaypalEmail("");
      setAccountOwner("");
      setBankname("");
      setIban("");
      setIsDefault(false);
    }
  }, [editingAccount, visible]);

  const validate = () => {
    if (type === "PAYPAL") {
      if (!paypalEmail?.trim()) {
        showErrorToast({ detail: "invalidemail" });
        return false;
      }
    } else {
      if (!accountOwner?.trim() || !bankname?.trim() || !iban?.trim()) {
        showErrorToast({ detail: "allFieldsRequired" });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      if (type === "BANK") {
        const ibanResult = await openIbanService.validate(iban);
        if (ibanResult.reason === "invalid") {
          showErrorToast({ detail: "invalidIban" });
          setSubmitting(false);
          return;
        }
      }

      if (isEdit) {
        const payload = {
          id: editingAccount.id,
          type,
          ...(type === "PAYPAL"
            ? { paypalEmail: paypalEmail.trim() }
            : { accountOwner: accountOwner.trim(), bankname: bankname.trim(), iban: iban.trim() }),
        };
        const res = await bankAccountService.update(payload);
        if (res.messageCode !== 200000 && res.messageCode !== 200001) {
          showErrorToast({ code: res.messageCode });
          setSubmitting(false);
          return;
        }
        if (isDefault) {
          await bankAccountService.setAsDefault(editingAccount.id);
        }
        showSuccessToast({ detail: "accountUpdatedSuccess" });
      } else {
        const payload = {
          type,
          ...(type === "PAYPAL"
            ? { paypalEmail: paypalEmail.trim() }
            : { accountOwner: accountOwner.trim(), bankname: bankname.trim(), iban: iban.trim() }),
        };
        const res = await bankAccountService.create(payload);
        if (res.messageCode !== 200000 && res.messageCode !== 200001) {
          showErrorToast({ code: res.messageCode });
          setSubmitting(false);
          return;
        }
        const newId = res.result?.id;
        if (isDefault && newId) {
          await bankAccountService.setAsDefault(newId);
        }
        showSuccessToast({ detail: "accountRegisteredSuccess" });
      }
      onSuccess?.();
      onHide?.();
    } catch (e) {
      showErrorToast({ code: e?.response?.data?.messageCode || 500000 });
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div className="flex  gap-2">
      <Button
        label={localeText("cancel")}
        className="flex-grow-1 border-round-lg text-sm font-medium"
        onClick={onHide}
        disabled={submitting}
        style={{
          backgroundColor: "var(--theme-background)",
          color: "var(--theme-text)",
          border: "1px solid var(--theme-border)",
          height: "40px",
        }}
      />
      <Button
        label={isEdit ? localeText("save") : localeText("bankAccountRegister")}
        className="flex-grow-1 border-round-lg text-sm font-medium"
        onClick={handleSubmit}
        loading={submitting}
        disabled={submitting}
        style={{
          backgroundColor: "var(--theme-primary)",
          color: "var(--theme-background)",
          height: "40px",
        }}
      />
    </div>
  );

  return (
    <Dialog
      header={
        <div className="flex flex-column gap-2">
          <div className="text-lg font-medium">
            {isEdit ? localeText("bankAccountFormTitleEdit") : localeText("bankAccountFormTitle")}
          </div>
          <div className="text-sm font-normal" style={{ color: "var(--theme-text-muted)" }}>
            {localeText("bankAccountFormSubtitle")}
          </div>
        </div>
      }
      headerStyle={{ backgroundColor: "var(--theme-background)", color: "var(--theme-text-color)" }}
      visible={visible}
      onHide={onHide}
      footer={footer}
      className="w-30rem mx-2"
      headerClassName="p-4"
      contentClassName="p-0"
      footerClassName="p-4"
      draggable={false}
      closable={!submitting}
    >
      {submitting && (
        <div
          className="mb-3 p-3 border-round"
          style={{ backgroundColor: "#E3F2FD", color: "#1565C0" }}
        >
          {localeText("registeringAccount")}
        </div>
      )}
      <div className="flex flex-column">
        <div>
          <span
            className="text-xs font-medium uppercase block py-2 px-4"
            style={{
              color: "color-mix(in srgb, var(--theme-text-muted) 70%, var(--theme-background) 30%)",
              backgroundColor:
                "color-mix(in srgb, var(--theme-border) 50%, var(--theme-background) 50%)",
            }}
          >
            {localeText("identification")}
          </span>
          <div className="px-4 py-3">
            <label htmlFor="accountType" className="block text-sm mb-1 font-medium">
              {localeText("accountType")}
            </label>
            <Dropdown
              key={visible ? "open" : "closed"}
              appendTo={"self"}
              id="accountType"
              value={type}
              options={TYPE_OPTIONS}
              onChange={(e) => setType(e.value)}
              className="w-full p-no-outline border-round-lg"
              disabled={submitting}
            />
          </div>
        </div>
        <div>
          <span
            className="text-xs font-medium uppercase block py-2 px-4"
            style={{
              color: "color-mix(in srgb, var(--theme-text-muted) 70%, var(--theme-background) 30%)",
              backgroundColor:
                "color-mix(in srgb, var(--theme-border) 50%, var(--theme-background) 50%)",
            }}
          >
            {localeText("bankDetails")}
          </span>
          <div className="px-4 py-3">
            {type === "PAYPAL" ? (
              <div>
                <label htmlFor="paypalEmail" className="block text-sm mb-1 font-medium">
                  {localeText("email")}
                </label>
                <InputText
                  id="paypalEmail"
                  value={paypalEmail}
                  onChange={(e) => setPaypalEmail(e.target.value)}
                  className="w-full border-round-lg"
                  disabled={submitting}
                />
              </div>
            ) : (
              <div className="flex flex-column gap-3">
                <div>
                  <label htmlFor="accountOwner" className="block text-sm mb-1 font-medium">
                    {localeText("accountOwner")}
                  </label>
                  <InputText
                    id="accountOwner"
                    value={accountOwner}
                    onChange={(e) => setAccountOwner(e.target.value)}
                    className="w-full border-round-lg"
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label htmlFor="bankname" className="block text-sm mb-1 font-medium">
                    {localeText("bankName")}
                  </label>
                  <InputText
                    id="bankname"
                    value={bankname}
                    onChange={(e) => setBankname(e.target.value)}
                    className="w-full border-round-lg"
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label htmlFor="iban" className="block text-sm mb-1 font-medium">
                    {localeText("bankAccountNumber")}
                  </label>
                  <InputText
                    id="iban"
                    value={iban}
                    onChange={(e) => setIban(e.target.value)}
                    className="w-full border-round-lg"
                    disabled={submitting}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="px-4 mb-3">
          <div className="flex align-items-top gap-2 mt-2">
            <Checkbox
              size={24}
              inputId="mainAccount"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.checked)}
            />
            <div>
              <label className="text-sm font-medium" htmlFor="mainAccount">
                {localeText("mainAccount")}
              </label>
              <p
                className="text-xs font-normal mt-1 mb-0"
                style={{ color: "var(--theme-text-muted)" }}
              >
                {localeText("mainAccountHelp")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default BankAccountFormModal;
